import { AfterViewInit, Component, EventEmitter, Injector, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { debounceTime, filter, Subject, Subscription, takeUntil } from 'rxjs';
import { FormStatusEnum, InsertOrigin, ResourceType, Roles } from 'src/app/common/enumerations/enumerations';
import { Resource, ResourceRelation, ShieldingGroup } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { TOCA } from 'src/app/components/catalogs/beamline-catalog/resource/toca';
import { BeamlineRequiringApproval } from 'src/app/components/catalogs/bratoi/models/beamline-requiring-approval';
import { RelatedLinkBase } from 'src/app/components/catalogs/navigation-links/link/related-link-base';
import { User } from 'src/app/components/catalogs/user-catalog/services/user';
import { RelatedDocument } from 'src/app/services/documents/documents';
import { FormRestriction } from 'src/app/services/forms/form-restriction';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { Kind, RSF, RSFAmendment, RSFRelatedDocument, RSFRelatedDocumentType, RSFRelatedLink, RSFResourceRelation, RSFResourceRelationType, RSFRestriction, RSFRestrictionMapper, RSFRoleNotification, } from '../../rsf';
import { RsfMainS1SystemsAffectedComponent } from './rsf-main-s1-systems-affected/rsf-main-s1-systems-affected.component';
import { RsfMainSBaseComponent } from '../rsf-main-s-base/rsf-main-s-base.component';
import moment, { invalid } from 'moment';
import { Field } from '../../rsf.service';
import { RsfMainS1NotificationsComponent } from './rsf-main-s1-notifications/rsf-main-s1-notifications.component';
import { detectChanges } from 'src/app/modules/libs/utils';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { TesterToolsService } from 'src/app/services/environment/tester-tools.service';
import { BratoiService } from 'src/app/components/catalogs/bratoi/bratoi.service';

@Component({
  selector: 'rsf-main-s1',
  templateUrl: './rsf-main-s1.component.html',
  styleUrls: ['./rsf-main-s1.component.scss']
})
export class RsfMainS1Component extends RsfMainSBaseComponent implements OnChanges, OnInit, AfterViewInit {
  @Output() formValidChanged = new EventEmitter<boolean>();
  @Output() systemsAffectedChanged = new EventEmitter<{ rsfProxy: RSF | RSFAmendment, changes: string[] }>();

  @ViewChild(RsfMainS1SystemsAffectedComponent) systemsAffected?: RsfMainS1SystemsAffectedComponent;
  @ViewChild(RsfMainS1NotificationsComponent) notifications?: RsfMainS1NotificationsComponent;

  documents: RelatedDocument[] = [];
  parentForms: RelatedDocument[] = [];
  childrenForms: RelatedDocument[] = [];
  relatedLinks: RelatedLinkBase[] = [];

  mentions: any[] = [];
  unapprovedTocaMentionIds: number[] = [];
  selectedMentions?: any[];
  selectedLocations: Resource[] = [];
  selectedBeamlineLocations: Resource[] = [];
  selectedAcceleratorLocations: Resource[] = [];

  allShieldings: ResourceRelation[] = [];
  filteredShieldings: ResourceRelation[] = [];
  selectedShieldings: ResourceRelation[] = [];
  shieldingGroups!: ShieldingGroup[];

  resources!: Resource[];
  resourcesSubs!: Subscription;

  resourceRelations!: ResourceRelation[];
  resourceRelationsSubs!: Subscription;

  bratois!: BeamlineRequiringApproval[];
  bratoisSubs!: Subscription;

  tocas!: TOCA[];
  tocasSubs!: Subscription;

  rsfRelatedDocumentEnum = RSFRelatedDocumentType;

  ctrlASCCLinks = new FormControl();
  ctrlParentForms = new FormControl();
  ctrlChildForms = new FormControl();
  ctrlDescription = new FormControl();
  // ctrlLocations = new FormControl();
  ctrlRelatedLinks = new FormControl();
  ctrlRequester = new FormControl();
  ctrlInitDate = new FormControl();
  ctrlEndDate = new FormControl();
  ctrlRoleNotifications = new FormControl();
  ctrlRestrictions = new FormControl();

  field = Field;

  filteredRequesters?: User[] = [];
  formRestrictions?: FormRestriction[];
  formRestrictionsOriginal?: FormRestriction[];

  locationIDs?: number[];

  resourceRelationTypeEnum = RSFResourceRelationType;

  descriptionHasError = false;
  systemsAffectedHaserror = false;

  _settingValues?: boolean;

  locationAddedIDs?: number[];
  private pendingSystemsAffectedLocationAddedIDs: number[] = [];

  /** Running count of tester auto-fill runs, used to number appended description entries. */
  private autoFillEntryCount = 0;

  rsfProxy?: RSF | RSFAmendment | null = null;

  constructor(
    private environmentService: EnvironmentService,
    private testerToolsService: TesterToolsService,
    protected override injector: Injector,
    private bratoiService: BratoiService
  ) {
    super(injector);
  }

  ngOnInit() {
    this.changes$
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.status?.isLogView) {
          // Initialize the reactive controls so History Log snapshots remain fully read-only.
          this.initializeForm();
          this.setValues();
          return;
        }

        this.initializeForm();
        this.setValues();
        this.loadRelatedShieldings();
        this.loadResources();
        this.loadBratois();
        this.loadTocas();
        this.loadRequesters();
      });
  }

  override ngOnChanges(changes: SimpleChanges): void {
    setTimeout(() => this.changes$.next());
  }

  /**
   * Initializes controls that depend on child ViewChild components after the initial check completes.
   */
  ngAfterViewInit(): void {
    if (this.status?.isLogView) {
      return;
    }

    // Avoid changing the formGroup-bound template during the same change-detection pass.
    setTimeout(() => {
      this.initializeForm();
      this.setValues();
    });
  }

  override ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.tocasSubs?.unsubscribe();
    this.bratoisSubs?.unsubscribe();
    this.resourcesSubs?.unsubscribe();
    this.resourceRelationsSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  //#region InitForm
  /**
   * Determines whether Parent/Child RSF links are available for the selected Systems Affected values.
   * PPS by itself does not enable the links; any other system or PPS condition makes them available.
   *
   * @returns `true` when at least one Systems Affected condition beyond the PPS checkbox is selected.
   */
  hasSystemsAffectedBeyondPps(): boolean {
    const rsf = this.rsfProxy;
    return [
      rsf?.beamLine,
      rsf?.accelerator,
      rsf?.otherSystems,
      rsf?.shielding,
      rsf?.shieldingRemoveReplace,
      rsf?.shieldingModification,
      rsf?.shieldingModificationARC,
      rsf?.shieldingModificationBRC,
      rsf?.ppsMaintenance,
      rsf?.ppsModification,
      rsf?.ppsModificationARC,
      rsf?.ppsModificationBRC,
      rsf?.ppsAcis,
      rsf?.tombis,
      rsf?.ppsOther,
      rsf?.radMonitors,
      rsf?.radMonitorsMoving,
      rsf?.radMonitorsDisconnecting,
      rsf?.radMonitorsModifying,
      rsf?.radMonitorsTripPoint,
      rsf?.radSurveyRequired,
      rsf?.ppsplcSet,
      rsf?.ppsSoftware,
      rsf?.etiAccess,
      rsf?.tombisplcSet,
      rsf?.tombisSoftware,
      rsf?.topOffInterlockSetPoint
    ].some(Boolean);
  }

  /**
   * Initializes the reactive form group for the RSF Main S1 component.
   *
   * This method sets up the form controls using the FormBuilder, including controls for ASCC links,
   * parent/child forms, descriptions, locations, related links, and various system-affected controls.
   * It also subscribes to form value changes to update the dirty state of the form.
   *
   * The form is enabled or disabled based on the component's `disabled` state, user privileges,
   * and the current status (creating or editing).
   *
   * @remarks
   * - Controls related to `systemsAffected` are initialized with their respective values or as new `FormControl` instances if not present.
   * - The form's enabled/disabled state is determined by the user's privileges and the current operation mode.
   */
  initializeForm() {
    this.setFormDirty(false);
    this.formGroup = this.formBuilder.group({
      ctrlASCCLinks: this.ctrlASCCLinks,
      ctrlParentForms: this.ctrlParentForms,
      ctrlChildForms: this.ctrlChildForms,
      ctrlDescription: this.ctrlDescription,
      // ctrlLocations: this.ctrlLocations,
      ctrlRelatedLinks: this.ctrlRelatedLinks,
      ctrlBeamline: this.systemsAffected?.ctrlBeamline ?? new FormControl(),
      ctrlBeamlineLocations: this.systemsAffected?.ctrlBeamlineLocations ?? new FormControl(),
      ctrlAccelerator: this.systemsAffected?.ctrlAccelerator ?? new FormControl(),
      ctrlAcceleratorLocations: this.systemsAffected?.ctrlAcceleratorLocations ?? new FormControl(),
      ctrlShielding: this.systemsAffected?.ctrlShielding ?? new FormControl(),
      ctrlShlRemoveReplace: this.systemsAffected?.ctrlShlRemoveReplace ?? new FormControl(),
      ctrlShlModification: this.systemsAffected?.ctrlShlModification ?? new FormControl(),
      ctrlShlModificationARC: this.systemsAffected?.ctrlShlModificationARC ?? new FormControl(),
      ctrlShlModificationBRC: this.systemsAffected?.ctrlShlModificationBRC ?? new FormControl(),
      ctrlPPS: this.systemsAffected?.ctrlPPS ?? new FormControl(),
      ctrlPPSOther: this.systemsAffected?.ctrlPPSOther ?? new FormControl(),
      ctrlPPSOtherText: this.systemsAffected?.ctrlPPSOtherText ?? new FormControl(),
      ctrlPPSMaintenance: this.systemsAffected?.ctrlPPSMaintenance ?? new FormControl(),
      ctrlPPSModification: this.systemsAffected?.ctrlPPSModification ?? new FormControl(),
      ctrlPPSModificationARC: this.systemsAffected?.ctrlPPSModificationARC ?? new FormControl(),
      ctrlPPSModificationBRC: this.systemsAffected?.ctrlPPSModificationBRC ?? new FormControl(),
      ctrlPPSAcis: this.systemsAffected?.ctrlPPSAcis ?? new FormControl(),
      ctrlTombis: this.systemsAffected?.ctrlTombis ?? new FormControl(),
      ctrlPPSPLCSet: this.systemsAffected?.ctrlPPSPLCSet ?? new FormControl(),
      ctrlPPSSoftware: this.systemsAffected?.ctrlPPSSoftware ?? new FormControl(),
      ctrlETIAccess: this.systemsAffected?.ctrlETIAccess ?? new FormControl(),
      ctrlTOMBISPLCSet: this.systemsAffected?.ctrlTOMBISPLCSet ?? new FormControl(),
      ctrlTOMBISSoftware: this.systemsAffected?.ctrlTOMBISSoftware ?? new FormControl(),
      ctrlTopOffInterlock: this.systemsAffected?.ctrlTopOffInterlock ?? new FormControl(),
      ctrlRadMonitors: this.systemsAffected?.ctrlRadMonitors ?? new FormControl(),
      ctrlRadMonitorsMoving: this.systemsAffected?.ctrlRadMonitorsMoving ?? new FormControl(),
      ctrlRadMonitorsDisconnecting: this.systemsAffected?.ctrlRadMonitorsDisconnecting ?? new FormControl(),
      ctrlRadMonitorsTripPoint: this.systemsAffected?.ctrlRadMonitorsTripPoint ?? new FormControl(),
      ctrlRadMonitorsModifying: this.systemsAffected?.ctrlRadMonitorsModifying ?? new FormControl(),
      ctrlRequester: this.ctrlRequester ?? new FormControl(),
      ctrlInitDate: this.ctrlInitDate ?? new FormControl(),
      ctrlEndDate: this.ctrlEndDate ?? new FormControl(),
      ctrlRoleNotifications: this.ctrlRoleNotifications ?? new FormControl(),
      ctrlRestrictions: this.ctrlRestrictions ?? new FormControl(),
      ctrlRadSurvey: this.systemsAffected?.ctrlRadSurvey ?? new FormControl(),
      ctrlOtherSystem: this.systemsAffected?.ctrlOtherSystem ?? new FormControl(),
      ctrlOtherSystems: this.systemsAffected?.ctrlOtherSystems ?? new FormControl()
    });
    this.formGroup.disable();

    this.formGroup.markAsPristine();
    this.formGroup.valueChanges.pipe(filter(() => !this._settingValues)).subscribe(c => {
      this.setFormDirty(!this.formGroup.pristine);
      if (!this.formGroup.pristine || this.canSubmit())
        this.formValidate();
    });

    this.rsfProxy = this.status?.isViewingAmendment ? this.rsfAmendment : this.rsf;
    if (!this.disabled) {
      if (this.status?.isMinorAmendment) {
        // Minor amendment: description, dates, ASCC Links, and Related Documents are editable
        this.formGroup.disable();
        this.ctrlDescription.enable();
        this.ctrlInitDate.enable();
        this.ctrlEndDate.enable();
        this.ctrlASCCLinks?.enable();
        this.ctrlRelatedLinks?.enable();

        // Explicitly disable all restricted fields for minor amendment
        this.systemsAffected?.ctrlBeamline?.disable();
        this.systemsAffected?.ctrlBeamlineLocations?.disable();
        this.systemsAffected?.ctrlAccelerator?.disable();
        this.systemsAffected?.ctrlAcceleratorLocations?.disable();
        this.ctrlRoleNotifications?.disable();
        this.ctrlRequester?.disable();
        this.ctrlRestrictions?.disable();
        this.systemsAffected?.ctrlShielding?.disable();
        this.systemsAffected?.ctrlShlRemoveReplace?.disable();
        this.systemsAffected?.ctrlShlModification?.disable();
        this.systemsAffected?.ctrlShlModificationARC?.disable();
        this.systemsAffected?.ctrlShlModificationBRC?.disable();
        this.systemsAffected?.ctrlPPS?.disable();
        this.systemsAffected?.ctrlPPSOther?.disable();
        this.systemsAffected?.ctrlPPSOtherText?.disable();
        this.systemsAffected?.ctrlPPSMaintenance?.disable();
        this.systemsAffected?.ctrlPPSModification?.disable();
        this.systemsAffected?.ctrlPPSModificationARC?.disable();
        this.systemsAffected?.ctrlPPSModificationBRC?.disable();
        this.systemsAffected?.ctrlPPSAcis?.disable();
        this.systemsAffected?.ctrlTombis?.disable();
        this.systemsAffected?.ctrlPPSPLCSet?.disable();
        this.systemsAffected?.ctrlPPSSoftware?.disable();
        this.systemsAffected?.ctrlETIAccess?.disable();
        this.systemsAffected?.ctrlTOMBISPLCSet?.disable();
        this.systemsAffected?.ctrlTOMBISSoftware?.disable();
        this.systemsAffected?.ctrlTopOffInterlock?.disable();
        this.systemsAffected?.ctrlRadMonitors?.disable();
        this.systemsAffected?.ctrlRadMonitorsMoving?.disable();
        this.systemsAffected?.ctrlRadMonitorsDisconnecting?.disable();
        this.systemsAffected?.ctrlRadMonitorsTripPoint?.disable();
        this.systemsAffected?.ctrlRadMonitorsModifying?.disable();
        this.systemsAffected?.ctrlRadSurvey?.disable();
        this.systemsAffected?.ctrlOtherSystem?.disable();
        this.systemsAffected?.ctrlOtherSystems?.disable();
        this.ctrlParentForms?.disable();
        this.ctrlChildForms?.disable();
      } else {
        this.formGroup.enable();
        // Parent/Child RSF links require a Systems Affected condition beyond PPS alone.
        if (!this.hasSystemsAffectedBeyondPps()) {
          this.ctrlChildForms.disable();
          this.ctrlParentForms.disable();
        }
      }
    } else {
      this.formGroup.disable();
    }
  }

  //#region Validate
  /**
   * Validates the form controls and updates their error states based on the current RSF status and input values.
   *
   * This method checks various conditions such as required fields, valid dates, and affected systems.
   * It sets errors on form controls when validation fails and clears them when validation passes.
   * Emits the validity status of the form via the `formValidChanged` event.
   *
   * @returns {boolean} - Returns `true` if the form is valid, otherwise `false`.
   */
  formValidate() {
    if (this.status?.isLogView) {
      // History rendering is read-only; avoid live validation side effects during change detection.
      return true;
    }

    if (this.rsfProxy && this.status &&
      (this.status.isCreating || this.status.isCreatingAmendment || this.status.isEditing || this.status.isEditingAmendment)
    ) {

      // ── Minor amendment: validate only description and dates ──────────────
      if (this.status.isMinorAmendment) {
        const estimatedInitOn = moment(this.ctrlInitDate?.value);
        const estimatedEndOn = moment(this.ctrlEndDate?.value);

        if (!estimatedInitOn.isValid()) {
          this.ctrlInitDate?.setErrors({ invalidDate: true });
          this.ctrlInitDate?.markAsTouched();
        } else {
          this.ctrlInitDate?.setErrors(null);
        }

        if (!estimatedEndOn.isValid() || estimatedEndOn.isBefore(estimatedInitOn)) {
          this.ctrlEndDate?.setErrors({ invalidDate: true });
          this.ctrlEndDate?.markAsTouched();
        } else {
          this.ctrlEndDate?.setErrors(null);
        }

        if (!this.rsfProxy?.description) {
          this.ctrlDescription.setErrors({ required: true });
          this.ctrlDescription.markAsTouched();
          this.descriptionHasError = true;
        } else {
          this.ctrlDescription.setErrors(null);
          this.descriptionHasError = false;
        }

        const valid = !this.ctrlInitDate.invalid && !this.ctrlEndDate.invalid && !this.descriptionHasError;
        this.formValidChanged.emit(valid);
        return valid;
      }
      // ── End minor amendment path ──────────────────────────────────────────

      if (!this.ctrlRequester.value?.id) {
        this.ctrlRequester?.setErrors({ required: true });
        this.ctrlRequester?.markAsTouched();
      }

      const estimatedInitOn = moment(this.ctrlInitDate?.value);
      const estimatedEndOn = moment(this.ctrlEndDate?.value);
      const today = moment(new Date()).date();
      if (
        !estimatedInitOn.isValid() ||
        estimatedInitOn.isBefore(moment(today))
      ) {
        this.ctrlInitDate?.setErrors({ invalidDate: true });
        this.ctrlInitDate?.markAsTouched();
      } else {
        this.ctrlInitDate?.setErrors(null);
      }
      if (
        !estimatedEndOn.isValid() ||
        estimatedEndOn.isBefore(estimatedInitOn)
      ) {
        this.ctrlEndDate?.setErrors({ invalidDate: true });
        this.ctrlEndDate?.markAsTouched();
      } else {
        this.ctrlEndDate?.setErrors(null);
      }

      if (!this.status?.isViewingAmendment && !this.rsfProxy?.description) {
        this.ctrlDescription.setErrors({ required: true });
        this.ctrlDescription.markAsTouched();
        this.descriptionHasError = true;
      } else {
        this.ctrlDescription.setErrors(null);
        this.descriptionHasError = false;
      }

      if (this.systemsAffected?.ctrlBeamline.value) {
        if (!this.selectedBeamlineLocations.length) {
          this.systemsAffected.ctrlBeamline.setErrors({ required: true });
        }
        else {
          this.systemsAffected.ctrlBeamline.setErrors(null);
        }
      }

      if (this.systemsAffected?.ctrlAccelerator.value) {
        if (!this.selectedAcceleratorLocations.length) {
          this.systemsAffected.ctrlAccelerator.setErrors({ required: true });
        }
        else {
          this.systemsAffected.ctrlAccelerator.setErrors(null);
        }
      }

      if (this.systemsAffected?.ctrlOtherSystem.value) {
        if (!this.rsfProxy.rsfOtherSystems || !this.rsfProxy.rsfOtherSystems.length) {
          this.systemsAffected.ctrlOtherSystem.setErrors({ required: true });
          this.systemsAffected.ctrlOtherSystems.setErrors({ required: true });
        }
        else {
          this.systemsAffected.ctrlOtherSystem.setErrors(null);
          this.systemsAffected.ctrlOtherSystems.setErrors(null);
        }
      }

      if (!this.formRestrictions?.length) {
        this.ctrlRestrictions.setErrors({ required: true });
        this.ctrlRestrictions.markAsTouched();
      }
      else {
        this.ctrlRestrictions.setErrors(null);
      }


      // Systems Affected Validations
      if (!this.systemsAffected?.ctrlShielding.value && !this.systemsAffected?.ctrlPPS.value) {
        this.systemsAffectedHaserror = true;
        this.systemsAffected?.ctrlShielding.setErrors({ required: true });
        this.systemsAffected?.ctrlPPS.setErrors({ required: true });
      }
      else {
        this.systemsAffectedHaserror = false;
        this.systemsAffected?.ctrlShielding.setErrors(null);
        this.systemsAffected?.ctrlPPS.setErrors(null);
      }

      if (this.systemsAffected?.ctrlShielding.value) {
        if (!this.systemsAffected.ctrlShlModification.value && !this.systemsAffected.ctrlShlRemoveReplace.value) {
          this.systemsAffected.ctrlShielding.setErrors({ required: true });
        }
        else {
          this.systemsAffected.ctrlShielding.setErrors(null);
        }
      }

      if (this.systemsAffected?.ctrlShlModification.value) {
        if (!this.systemsAffected.ctrlShlModificationARC.value && !this.systemsAffected.ctrlShlModificationBRC.value) {
          this.systemsAffected.ctrlShlModification.setErrors({ required: true });
        }
        else {
          this.systemsAffected.ctrlShlModification.setErrors(null);
        }
      }

      if (this.systemsAffected?.ctrlPPS.value) {
        if (!this.systemsAffected.ctrlPPSMaintenance.value && !this.systemsAffected.ctrlPPSModification.value) {
          this.systemsAffected.ctrlPPS.setErrors({ required: true });
        }
        else {
          this.systemsAffected.ctrlPPS.setErrors(null);
        }
      }

      if (this.systemsAffected?.ctrlPPSOther.value) {
        if (!this.systemsAffected.ctrlPPSOtherText.value || !this.systemsAffected.ctrlPPSOtherText.value.trim().length) {
          this.systemsAffected.ctrlPPSOther.setErrors({ required: true });
          this.systemsAffected.ctrlPPSOtherText.setErrors({ required: true });
        }
        else {
          this.systemsAffected.ctrlPPSOther.setErrors(null);
          this.systemsAffected.ctrlPPSOtherText.setErrors(null);
        }
      }

      if (this.systemsAffected?.ctrlRadMonitors.value) {
        if (!this.systemsAffected.ctrlRadMonitorsDisconnecting.value && !this.systemsAffected.ctrlRadMonitorsModifying.value && !this.systemsAffected.ctrlRadMonitorsMoving.value && !this.systemsAffected.ctrlRadMonitorsTripPoint.value) {
          this.systemsAffected.ctrlRadMonitors.setErrors({ required: true });
        }
        else {
          this.systemsAffected?.ctrlRadMonitors.setErrors(null);
        }
      }

      if (!this.rsfProxy.beamLine && !this.rsfProxy.accelerator && !this.rsfProxy.otherSystems && !this.formGroup.pristine) {
        this.systemsAffectedHaserror = true;
        this.formGroup.setErrors({ invalid: true });
      }
      else {
        this.systemsAffectedHaserror = false;
      }



    } else {
      this.formGroup.setErrors(null);
      this.ctrlDescription.setErrors(null);
      this.descriptionHasError = false;
      this.systemsAffectedHaserror = false;
      this.formGroup.markAsPristine();
      this.formGroup.markAsUntouched();
    }
    const valid = this.formGroup.valid && !this.descriptionHasError;
    this.formValidChanged.emit(valid);
    return valid;
  }

  setRSFProxy() {
    if (!this.status?.isViewingAmendment && this.rsf) {
      this.rsfProxy = this.rsf;
      this.rsfProxyUnchanged = this.utils.cloneDeep(this.rsf);
    }
    else if (this.status?.isViewingAmendment && this.rsfAmendment) {
      this.rsfProxy = this.rsfAmendment;
      this.rsfProxyUnchanged = this.utils.cloneDeep(this.rsfAmendment);
    }
    else {
      this.rsfProxy = null;
      this.rsfProxyUnchanged = null;
    }
  }

  rsfProxyUnchanged: RSF | RSFAmendment | null = null;

  //#region Set Values
  /**
   * Initializes and sets the component's form controls and related properties based on the current RSF data.
   *
   * This method performs the following actions:
   * - Populates selected locations, beamline locations, and accelerator locations from RSF resource relations.
   * - Sets related documents (ASCC links, parent forms, child forms) from RSF related documents.
   * - Maps related links from RSF related links.
   * - Updates various form controls in the `systemsAffected` group and other controls with corresponding RSF values.
   * - Sets form restrictions using the RSF restrictions.
   * - Calls helper methods to update location IDs and reset the form's dirty state.
   *
   * Should be called whenever the RSF data is loaded or needs to be reflected in the form.
   */
  setValues() {
    this._settingValues = true;
    this.setRSFProxy();

    this.documents = this.rsfProxy?.rsfRelatedDocuments
      .filter(r => r.type == RSFRelatedDocumentType.ASCCForm).map(r => r.relatedDocument) ?? [];
    this.parentForms = this.rsfProxy?.rsfRelatedDocuments
      .filter(r => r.type == RSFRelatedDocumentType.ParentForm).map(r => r.relatedDocument) ?? [];
    this.childrenForms = this.rsfProxy?.rsfRelatedDocuments
      .filter(r => r.type == RSFRelatedDocumentType.ChildForm).map(r => r.relatedDocument) ?? [];

    this.relatedLinks = this.rsfProxy?.rsfRelatedLinks?.map(r => { return { id: r.id, formID: r.formID, text: r.text, url: r.url }; }) ?? [];

    this.systemsAffected?.ctrlBeamline.setValue(this.rsfProxy?.beamLine);
    this.systemsAffected?.ctrlAccelerator.setValue(this.rsfProxy?.accelerator);
    this.systemsAffected?.ctrlOtherSystem.setValue(this.rsfProxy?.otherSystems);
    this.systemsAffected?.ctrlShielding.setValue(this.rsfProxy?.shielding);
    this.systemsAffected?.ctrlShlRemoveReplace.setValue(this.rsfProxy?.shieldingRemoveReplace);
    this.systemsAffected?.ctrlShlModification.setValue(this.rsfProxy?.shieldingModification);
    this.systemsAffected?.ctrlShlModificationARC.setValue(this.rsfProxy?.shieldingModificationARC);
    this.systemsAffected?.ctrlShlModificationBRC.setValue(this.rsfProxy?.shieldingModificationBRC);
    this.systemsAffected?.ctrlPPS.setValue(this.rsfProxy?.pps);
    this.systemsAffected?.ctrlPPSMaintenance.setValue(this.rsfProxy?.ppsMaintenance);
    this.systemsAffected?.ctrlPPSModification.setValue(this.rsfProxy?.ppsModification);
    this.systemsAffected?.ctrlPPSModificationARC.setValue(this.rsfProxy?.ppsModificationARC);
    this.systemsAffected?.ctrlPPSModificationBRC.setValue(this.rsfProxy?.ppsModificationBRC);
    this.systemsAffected?.ctrlPPSAcis.setValue(this.rsfProxy?.ppsAcis);
    this.systemsAffected?.ctrlTombis.setValue(this.rsfProxy?.tombis);
    this.systemsAffected?.ctrlPPSOther.setValue(this.rsfProxy?.ppsOther);
    this.systemsAffected?.ctrlPPSOtherText.setValue(this.rsfProxy?.ppsOtherText);

    this.systemsAffected?.ctrlPPSPLCSet.setValue(this.rsfProxy?.ppsplcSet);
    this.systemsAffected?.ctrlPPSSoftware.setValue(this.rsfProxy?.ppsSoftware);
    this.systemsAffected?.ctrlETIAccess.setValue(this.rsfProxy?.etiAccess);
    this.systemsAffected?.ctrlTOMBISPLCSet.setValue(this.rsfProxy?.tombisplcSet);
    this.systemsAffected?.ctrlTOMBISSoftware.setValue(this.rsfProxy?.tombisSoftware);
    this.systemsAffected?.ctrlTopOffInterlock.setValue(this.rsfProxy?.topOffInterlockSetPoint);
    this.systemsAffected?.ctrlRadMonitors.setValue(this.rsfProxy?.radMonitors);
    this.systemsAffected?.ctrlRadMonitorsMoving.setValue(this.rsfProxy?.radMonitorsMoving);
    this.systemsAffected?.ctrlRadMonitorsDisconnecting.setValue(this.rsfProxy?.radMonitorsDisconnecting);
    this.systemsAffected?.ctrlRadMonitorsModifying.setValue(this.rsfProxy?.radMonitorsModifying);
    this.systemsAffected?.ctrlRadMonitorsTripPoint.setValue(this.rsfProxy?.radMonitorsTripPoint);

    this.systemsAffected?.ctrlRadSurvey.setValue(this.rsfProxy?.radSurveyRequired);

    this.ctrlRequester.setValue(this.rsfProxy?.requester);
    this.ctrlInitDate.setValue(this.rsfProxy?.estimatedStartOn);
    this.ctrlEndDate.setValue(this.rsfProxy?.estimatedEndOn);

    this.ctrlRoleNotifications.setValue(this.rsfProxy?.rsfRoleNotifications);

    this.formRestrictions = RSFRestrictionMapper.fromRSF(this.rsfProxy?.rsfRestrictions, this.rsf?.rsfRestrictions);
    this.ctrlRestrictions.setValue(this.formRestrictions);

    this.createSelectedLocations(false);

    this._settingValues = false;
    this.getLocationIDs();
    this.locationAddedIDs = [];
    this.setFormDirty(false);
    this.formValidate();
  }

  /**
     * Rebuilds selected beamline and accelerator locations from RSF relations.
     * Emits only the newly added location IDs so the restriction table can add missing restrictions once.
     * @param updateIDs - Whether to populate `locationAddedIDs` for auto-restriction handling.
     * @param previousLocationIDs - The location IDs selected before the current change.
     */
  createSelectedLocations(updateIDs = true, previousLocationIDs = this.getSelectedLocationIDs()) {
    const resourceRelations = this.rsfProxy?.rsfResourceRelations ?? [];
    const restrictions = this.rsfProxy?.rsfRestrictions ?? [];
    this.selectedBeamlineLocations = this.toSelectedResources(RSFResourceRelationType.Beamline, resourceRelations, restrictions);
    this.selectedAcceleratorLocations = this.toSelectedResources(RSFResourceRelationType.Accelerator, resourceRelations, restrictions);

    this.selectedLocations = this.selectedAcceleratorLocations.concat(this.selectedBeamlineLocations);
    // Emit only the locations added by the latest action so missing restrictions auto-populate.
    this.locationAddedIDs = updateIDs ? this.getSelectedLocationIDs().filter(id => !previousLocationIDs.includes(id)) : [];

    this.checkSelectedLocations();
  }

  /**
   * Gets the currently selected RSF beamline and accelerator location IDs.
   * @returns Selected location IDs with empty values removed.
   */
  private getSelectedLocationIDs(): number[] {
    return this.selectedBeamlineLocations
      .map(b => b.id)
      .concat(this.selectedAcceleratorLocations.map(a => a.id))
      .filter((id): id is number => !!id);
  }

  /**
   * Captures the resource-chip emitted location ID before the parent rebuilds selected locations.
   * @param locationID - Resource ID emitted by the Systems Affected child after a single location is added.
   */
  onLocationAddedID(locationID: number): void {
    this.pendingSystemsAffectedLocationAddedIDs = [locationID];
    this.locationAddedIDs = [locationID];
  }

  /**
   * Transforms RSF resource relations into an array of Resource objects with restriction metadata.
   *
   * Filters relations by type and maps each to a Resource with two additional properties:
   * - `disabled`: Set to true if the resource is restricted
   * - `insertOrigin`: Preserved from the original relation
   *
   * @param type - The RSFResourceRelationType to filter by (e.g., Beamline, Accelerator)
   * @param resourceRelations - Array of RSF resource relations to process
   * @param restrictions - Array of RSF restrictions to check against for disabled state
   * @returns Array of Resource objects with disabled and insertOrigin properties
   */
  toSelectedResources = (type: RSFResourceRelationType, resourceRelations: RSFResourceRelation[], restrictions: RSFRestriction[]) =>
    resourceRelations
      .filter(r => r.type === type)
      .map(r => {
        const resource = this.resources?.find(res => res.id === r.resourceID) ?? r.resource;
        return {
          ...(resource ?? {} as Resource),
          name: this.formatResourceName(resource),
          disabled: restrictions.some(fr => fr.restriction?.resourceID === r.resourceID || resource?.parentResources?.map(pr => pr.parentResourceID).includes(fr.restriction?.resourceID)),
          insertOrigin: r.insertOrigin,
        };
      }
      );

  /**
   * Appends the parent shutter name to the resource name if applicable.
   * @param resource - The Resource object to format
   */
  formatResourceName(resource?: Resource) {
    const shutter = resource?.parentResources?.find(pr => pr.parentResourceType == ResourceType.Shutter)?.parentResource;
    const beamline = resource?.childResources?.find(c => c.childResourceType == ResourceType.Beamline)?.childResource;
    if (shutter && !resource.name?.includes('/')) {
      resource.name = `${resource.name} / ${shutter.name}`;
    }
    else if (resource && beamline && !resource?.name?.includes('/')) {
      resource.name = `${beamline.name} / ${resource.name}`;
    }
    return resource?.name;
  }

  /**
   * Handles changes to the related documents of a specific type for the current RSF.
   *
   * Preserves persisted relationship IDs for documents that remain selected, while creating relationship records
   * only for newly selected documents. Updates the RSF's related documents list accordingly.
   *
   * @param e - An array of `RelatedDocument` objects representing the new or updated related documents.
   * @param type - The `RSFRelatedDocumentType` indicating the type of related documents being changed.
   */
  onRelatedDocumentChanged(e: RelatedDocument[], type: RSFRelatedDocumentType) {
    const existingDocuments = this.rsfProxy?.rsfRelatedDocuments.filter(r => r.type == type) ?? [];
    const rsfRelatedDocuments = this.rsfProxy?.rsfRelatedDocuments.filter(r => r.type != type) ?? [];

    e.forEach(r => {
      if (r) {
        // Retain relationship and RelatedDocument IDs so the backend does not remove/re-add unchanged links.
        const existingDocument = existingDocuments.find(document =>
          document.relatedDocumentID == r.id ||
          (document.relatedDocument?.relatedDocumentId == r.relatedDocumentId &&
            document.relatedDocument?.relatedDocumentTypeId == r.relatedDocumentTypeId)
        );
        const rsfRelatedDocument = {
          ...existingDocument,
          rsfID: this.rsfProxy?.id,
          rsfAmendmentID: this.rsfAmendment?.id,
          relatedDocumentID: existingDocument?.relatedDocumentID ?? r.id ?? 0,
          relatedDocument: r,
          relatedDocumentType: r.relatedDocumentTypeId,
          type
        } as RSFRelatedDocument;
        rsfRelatedDocuments.push(rsfRelatedDocument);
      }

    });
    if (this.rsfProxy) {
      this.rsfProxy.rsfRelatedDocuments = rsfRelatedDocuments;
      this.setFormDirty();
      this.formValidate();
    }
  }

  /**
   * Handles changes to the related links selection.
   *
   * Updates the component's `relatedLinks` property, synchronizes the form control value,
   * and maps the updated related links to the `rsf.rsfRelatedLinks` property.
   *
   * @param e - An array of `RelatedLinkBase` objects representing the updated related links.
   */
  onRelatedLinksChanged(e: RelatedLinkBase[]) {
    this.setFormDirty();
    this.relatedLinks = e;
    this.ctrlRelatedLinks.setValue(this.relatedLinks);
    this.rsfProxy!.rsfRelatedLinks = this.relatedLinks.map(r => {
      const rsfRelatedLink = {
        id: r.id ?? 0,
        text: r.text,
        url: r.url,
        rsfID: this.rsf?.id,
        rsfAmendmentID: this.rsfAmendment?.id
      } as RSFRelatedLink;
      return rsfRelatedLink;
    });
    this.formValidate();
  }

  /**
   * Subscribes to the store's Resources.data observable and loads resource-related data when available.
   *
   * The method:
   * - Selects the Resources.data slice from the NgRx store.
   * - Filters out empty arrays so downstream logic only runs when there is at least one resource.
   * - Subscribes to the resulting stream, assigning the latest data to `this.resources`.
   * - Calls `this.loadResourceRelations()` once data is received.
   * - Stores the resulting Subscription in `this.resourcesSubs` so it can be unsubscribed later to avoid memory leaks.
   *
   * Note: This method has side effects (modifies component state and creates a subscription) and does not return a value.
   *
   * @returns void
   */
  loadResources() {
    this.resourcesSubs?.unsubscribe();
    this.resourcesSubs = this.store
      .select(state => state.Resources.data)
      .pipe(filter(data => !!data.length))
      .subscribe(data => {
        this.resources = data;
        this.loadResourceRelations();
      });
  }

  /**
   * Subscribes to the store slice containing resource relations and synchronizes local state.
   *
   * Subscribes to `state.ResourceRelations.data`, filters out empty emissions, and:
   * - stores the subscription in `this.resourceRelationsSubs`,
   * - assigns the emitted array to `this.resourceRelations`,
   * - for each relation, resolves and assigns its `parentResource` by matching `parentResourceID`
   *   against `this.resources` (uses optional chaining to tolerate missing `this.resources`).
   *
   * Remarks:
   * - The method mutates each relation in-place by setting `parentResource`.
   * - The subscription will only react to non-empty arrays; callers are responsible for
   *   unsubscribing (for example in `ngOnDestroy`) to avoid memory leaks.
   *
   * Side effects:
   * - Reads from `this.store` and `this.resources`.
   * - Writes to `this.resourceRelations` and the `parentResource` property of each relation.
   *
   * @returns void
   */
  loadResourceRelations() {
    this.resourceRelationsSubs?.unsubscribe();
    this.resourceRelationsSubs = this.store
      .select(state => state.ResourceRelations.data)
      .pipe(filter(data => !!data.length))
      .subscribe((data) => {
        this.resourceRelations = data;
        this.resourceRelations.map(r => r.parentResource = this.resources?.find(x => x.id == r.parentResourceID));
        this.refreshRelatedShieldings();
      });
  }

  /**
   * Rebuilds shielding mention options after dependent catalog data is available.
   */
  private refreshRelatedShieldings(): void {
    if (!this.resources?.length || !this.selectedLocations) {
      return;
    }

    this.loadRelatedShieldings();
  }

  /**
   * Loads and processes shielding data related to the currently selected locations.
   *
   * For each selected location, this method:
   * - Finds the corresponding resource from the available resources.
   * - If the resource is a branchline with child resources, normalizes it to its parent accelerator location.
   * - Adds shutter, accelerator, and location shieldings for the resource.
   * - Adds TOCAS (presumably a type of shielding or related data) for the location.
   *
   * After processing all selected locations, filters the collected shieldings.
   *
   * @remarks
   * This method modifies the `allShieldings` property and relies on the `selectedLocations` and `resources` properties.
   */
  loadRelatedShieldings() {
    // Existing unapproved TOCA mentions remain visible but are struck out in the description.
    this.unapprovedTocaMentionIds = this.bratoiService.getUnapprovedTocaMentionIds(this.bratois);
    this.allShieldings = [];

    this.selectedLocations?.forEach(location => {
      let resource = this.resources?.find(x => x.id === location?.id);

      if (!resource) return;

      // Normalize branchline -> accelerator location
      if (resource.type === ResourceType.Branchline && resource.childResources?.length) {
        resource = this.resources?.find(r => r.id === resource?.childResources?.[0].parentResourceID) ?? resource;
      }

      this.addShutterShieldings(resource);
      this.addAcceleratorShieldings(resource);
      this.addLocationShieldings(resource);
      this.addTocas(location);
    });

    this.filterShieldings();
  }

  /**
   * Adds all Shielding child resources associated with a given Shutter resource to the `allShieldings` array.
   *
   * This method determines the relevant Shutter resource from the provided `resource` parameter,
   * either directly (if the resource is a Shutter) or by finding its parent Shutter. It then collects
   * all Shielding child resources of that Shutter, marks each as shared if it has multiple parent relations,
   * sets their type and type name, sorts them by name, and appends them to the `allShieldings` array.
   *
   * @param resource - The resource from which to find associated Shielding child resources.
   */
  private addShutterShieldings(resource: Resource) {
    let shutterResource = null;
    if (resource.type == ResourceType.Shutter) shutterResource = resource;
    else {
      const shutterParent = resource?.parentResources?.find(pr => pr.parentResourceType === ResourceType.Shutter);
      shutterResource = this.resources?.find(r => r.id === shutterParent?.parentResourceID);
    }
    const shieldings = shutterResource?.childResources?.filter(
      cr => cr.childResourceType === ResourceType.Shielding
    ) ?? [];

    shieldings.forEach(s => {
      const parents = this.resourceRelations?.filter(x => x.childResourceID === s.childResourceID) ?? [];
      s.shared = parents.length > 1;
      s.childResourceType = ResourceType.Shielding;
      s.childResourceTypeName = "Shielding";
    });

    this.allShieldings = [
      ...this.allShieldings,
      ...shieldings.sort((a, b) => this.utils.SortBeamlines(a.childResourceName, b.childResourceName))
    ];
  }

  /**
   * Adds shielding resources associated with the accelerator parent of the given resource to the `allShieldings` array.
   *
   * This method locates the accelerator parent of the specified resource, finds all shielding child resources
   * of that accelerator which are linked to the given resource's ID, and updates their properties to reflect
   * sharing status and type information. The processed shieldings are then merged into the `allShieldings` array.
   *
   * @param resource - The resource for which associated accelerator shieldings are to be added.
   */
  private addAcceleratorShieldings(resource: Resource) {
    const acceleratorParentId = resource?.parentResources?.find(x => x.parentResourceType === ResourceType.Accelerator)?.parentResourceID;
    const accShieldings = this.resources
      ?.find(r => r.id === acceleratorParentId)
      ?.childResources
      ?.filter(x => x.childResourceType === ResourceType.Shielding)
      ?.filter(x => (this.utils.JSONparse(x.locationsIDs) as number[]).includes(resource?.id ?? 0)) ?? [];

    accShieldings.forEach(s => {
      const parents = this.resources?.find(r => r.id === s.childResourceID)?.parentResources ?? [];
      s.shared = parents.length > 1;
      s.sharedWith = parents.filter(rr => rr.id !== s.id).map(rr => rr.locationsIDs ?? '');
      s.childResourceType = ResourceType.Shielding;
      s.childResourceTypeName = ResourceType[ResourceType.Shielding];
    });

    this.allShieldings = [...this.allShieldings, ...accShieldings];
  }

  private addLocationShieldings(resource: Resource) {
    if (resource?.type !== ResourceType.AcceleratorLocation) return;

    const locationShieldings = this.resourceRelations?.filter(r =>
      (this.utils.JSONparse(r.locationsIDs) as number[])?.includes(resource.id)
    ) ?? [];

    const existingIds = new Set(this.allShieldings.map(s => s.id));

    locationShieldings.forEach(x => {
      if (!existingIds.has(x.id)) {
        x.childResourceType = ResourceType.AcceleratorLocation;
        x.childResourceTypeName = "Accelerator Location";
        this.allShieldings.push(x);
      }
    });
  }

  /**
   * Adds TOCA resource relations associated with the given location to the `allShieldings` array,
   * ensuring no duplicates based on `childResourceID`.
   *
   * This method finds TOCA resources (`tocas`) related to the specified `location` from the
   * active TOCA and BRATOI stores.
   * For each TOCA, if its negative ID does not already exist in `allShieldings`, a new
   * `ResourceRelation` is created and added to `allShieldings`.
   *
   * @param location - The resource for which associated TOCA relations should be added.
   */
  private addTocas(location: Resource) {
    const tocas = this.getLocationTocas(location);

    const existingIds = new Set(this.allShieldings.map(x => x.childResourceID));

    tocas.forEach(t => {
      const tocaId = -t.id;
      if (!existingIds.has(tocaId)) {
        const toca: ResourceRelation = {
          childResourceID: tocaId,
          childResourceName: t.name,
          shared: true,
          position: 0,
          childResourceType: ResourceType.TOCA,
          childResourceTypeName: ResourceType[ResourceType.TOCA],
        };
        this.allShieldings.push(toca);
      }
    });
  }

  /**
   * Finds active TOCAs tied to the selected location using loaded TOCA relations.
   * @param location - Beamline or accelerator location being used to build mention options.
   * @returns TOCAs that should be available in the work description mention list.
   */
  private getLocationTocas(location: Resource): TOCA[] {
    // SCF and RSF share approval gating and catalog fallback through the common TOCA resolver.
    return this.bratoiService.resolveAvailableTocas(location, this.bratois, this.tocas);
  }

  /**
   * Loads BRATOI rows so shared TOCAs can be resolved beyond catalog-only TOCA relations.
   */
  loadBratois() {
    this.bratoisSubs?.unsubscribe();
    this.bratoisSubs = this.store
      .select(state => state.Bratoi.data)
      .pipe(filter(data => !!data?.length))
      .subscribe((data) => {
        this.bratois = data;
        this.refreshRelatedShieldings();
      });
  }

  loadTocas() {
    this.tocasSubs?.unsubscribe();
    this.tocasSubs = this.store
      .select((state) => state.TOCA.data)
      .subscribe((data) => {
        this.tocas = data;
        this.refreshRelatedShieldings();
      });
  }

  /**
   * Filters the available shieldings by removing those that have already been selected.
   *
   * This method removes any shieldings from `allShieldings` that exist in `selectedShieldings`
   * (based on matching `childResourceID`), creating a filtered list of available shieldings
   * that can still be added. It then invokes `createShieldingGroups()` to reorganize the
   * filtered shieldings into logical groupings.
   *
   * @remarks
   * - Modifies the `filteredShieldings` property to contain only non-selected shieldings.
   * - Calls `createShieldingGroups()` after filtering to regenerate shielding groups.
   * - Uses optional chaining (?.) to safely handle potentially undefined `childResourceID` values.
   *
   * @returns void
   */
  filterShieldings() {
    this.filteredShieldings = this.allShieldings.filter(
      (x) =>
        !this.selectedShieldings
          .map((s) => s?.childResourceID)
          .includes(x?.childResourceID)
    );
    this.createShieldingGroups();
  }

  /**
   * Generates and populates the `shieldingGroups` array based on the current `filteredShieldings`,
   * `resourceRelations`, and `selectedLocations`. The method groups shieldings by resource type,
   * ensuring no duplicates, and associates related shieldings for each group.
   *
   * - For items of type `Accelerator`, it creates a group per unique accelerator, associating
   *   shieldings that are related but not already selected.
   * - For other resource types (excluding `AcceleratorLocation`), it creates a group per unique type,
   *   aggregating all shieldings of that type.
   *
   * After grouping, the method sorts the groups by their type and invokes `getMentions()` to update
   * any related mentions.
   */
  createShieldingGroups() {
    this.shieldingGroups = [];
    this.filteredShieldings.map((l) => {
      if (l.childResourceType == ResourceType.Accelerator) {
        if (!this.shieldingGroups.map((g) => g.id).includes(l.id)) {
          const relations = this.resourceRelations?.filter(
            (rr) =>
              rr.parentResourceID == l.id &&
              !this.selectedLocations?.map((x) => x.id).includes(rr.childResourceID!)
          );
          const shieldings = relations?.filter(
            (l) => l.childResourceType == ResourceType.AcceleratorLocation
          );
          this.shieldingGroups.push({
            name: l.childResourceName,
            type: l.childResourceType,
            id: l.id,
            shieldings,
          });
        }
      } else {
        if (
          !this.shieldingGroups.map((g) => g.type).includes(l.childResourceType ?? 0) && l.childResourceType != ResourceType.AcceleratorLocation) {
          this.shieldingGroups.push({
            name: l.childResourceTypeName,
            type: l.childResourceType,
            shieldings: this.filteredShieldings.filter(
              (x) => x.childResourceType == l.childResourceType
            ),
          });
        }
      }
    });
    this.shieldingGroups = this.shieldingGroups.sort((a, b) => (a.type ?? 0) - (b.type ?? 0));
    this.getMentions();
  }

  //#region Mentions
  /**
   * Populates the `mentions` property with a flattened list of group and shielding items
   * derived from the `shieldingGroups` property. Each group is represented as a disabled
   * group item, followed by its associated shielding items. Shielding items include
   * relevant metadata such as resource types, shared status, and descriptive titles.
   *
   * The resulting `mentions` array contains both group and shielding objects, each with
   * properties tailored for mention/autocomplete UI components.
   *
   * @remarks
   * - Group items are marked as disabled and are not shared.
   * - Shielding items inherit sharing and descriptive properties from their source objects.
   * - If `shieldingGroups` is undefined or empty, `mentions` will not be modified.
   */
  getMentions() {
    if (this.shieldingGroups) {
      this.mentions = this.shieldingGroups.flatMap(group => {
        // Create the group item with necessary properties
        const groupItem = {
          id: `group-${group.id || group.type}`, // Unique ID for the group
          value: group.name, // Group name
          isGroup: true, // Mark it as a group
          parentResourceType: group.type, // Assuming 'type' is the parentResourceType for the group
          childResourceType: undefined, // No childResourceType for the group itself
          shared: false, // Groups aren't shared, adjust if needed
          sharedWith: [], // Empty array for sharedWith, adjust if needed
          disabled: true,
          title: ''
        };

        const shieldingItems = group.shieldings ? group.shieldings.map((shielding) => {
          const resource = this.resources.find(x => x.id === shielding.childResourceID);
          const links = resource?.windchillLinks;
          return {
            id: shielding.childResourceID,
            value: shielding.childResourceName,
            parentGroup: group.name,
            parentResourceType: shielding.parentResourceType,
            childResourceType: shielding.childResourceType,
            shared: shielding.shared,
            sharedWith: shielding.sharedWith,
            title: resource?.keText,
            windchillLinks: resource?.windchillLinks
          };
        }) : [];

        return [groupItem, ...shieldingItems]; // Combine the group and its shieldings
      });
    }
  }


  /**
   * Handles changes to the selected mentions, updating the selected shieldings and TOCAs accordingly.
   *
   * This method processes the new mentions selection by:
   * - Identifying and adding new shielding resources based on positive mention values.
   * - Removing shieldings that are no longer selected.
   * - Identifying and adding new TOCA resources based on negative mention values.
   * - Removing TOCAs that are no longer selected.
   *
   * @param e - The new selection of mentions, typically an array of numeric IDs where positive values represent shieldings and negative values represent TOCAs.
   */
  mentionsChanged(e: any) {
    const previousMentionLocationIDs = this.getSelectedLocationIDs();
    this.selectedMentions = e;
    const selectedShielding = this.selectedMentions?.filter(m => m > 0);
    const selectedTocas = this.selectedMentions?.filter(m => m < 0);

    // Handle added shielding
    const addedShieldings = (selectedShielding || [])
      .map(mention =>
        this.getMentionedShielding(mention)
      )
      .filter((r): r is ResourceRelation => r !== undefined); // Type guard
    addedShieldings.forEach(option => this.shieldingSelected(option));
    // Mentioning a shielding in the Work Description implies shielding work, so auto-check the Shielding Type of Work.
    if (addedShieldings.length) {
      this.autoCheckShieldingTypeOfWork();
    }

    // Handle removed shielding
    const removedShieldings = this.selectedShieldings.filter(s => (s.childResourceID ?? 0) > 0 && !selectedShielding?.includes(s.childResourceID));
    removedShieldings.forEach(shielding => this.removeShl(shielding));

    // Handle added TOCAs
    const addedTocas = (selectedTocas || [])
      .map(mention =>
        this.findToca(-mention, this.selectedLocations).find(t =>
          t.id === -mention &&
          !this.selectedShieldings.some(s => s.childResourceID === -t.id)  // Exclude if it's in shieldings
        )
      )
      .filter((t): t is TOCA => t !== undefined); // Type guard to remove undefined

    addedTocas.forEach(a => {
      const addedResourceRelation = {
        childResourceID: -a.id,
        childResourceType: this.resourceTypeEnum.TOCA,
        childResourceName: a.name
      } as ResourceRelation;
      this.shieldingSelected(addedResourceRelation);
    });

    // Handle removed TOCAs
    const removedTocas = this.selectedShieldings.filter(s => !selectedTocas?.includes(s.childResourceID)).filter(m => m.childResourceType == this.resourceTypeEnum.TOCA);
    removedTocas.forEach(shielding => this.removeShl(shielding));

    // Emit the full set of locations added by this mention change so restrictions are not lost when multiple mentions are selected together.
    this.locationAddedIDs = this.getSelectedLocationIDs().filter(id => !previousMentionLocationIDs.includes(id));
  }

  /**
   * Checks the Shielding Type of Work after a shielding is mentioned in the Work Description.
   * Skips read-only and Minor Amendment forms, and never unchecks a selection the user already made.
   * Side effect: syncs the RSF proxy through Systems Affected so shielding sub-options are enabled.
   */
  private autoCheckShieldingTypeOfWork(): void {
    const ctrlShielding = this.systemsAffected?.ctrlShielding;
    if (!ctrlShielding || ctrlShielding.value || this.disabled || this.status?.isMinorAmendment) {
      return;
    }

    ctrlShielding.setValue(true);
    this.systemsAffected?.onSystemChanged();
  }

  /**
   * Resolves a work-description shielding mention to the same relation used by the mention dropdown.
   * Prefer `allShieldings` because it carries the shared shielding metadata needed to add related locations.
   * @param mention - Positive shielding resource ID from the comments-box mention list.
   * @returns The matching shielding relation when it is not already selected.
   */
  private getMentionedShielding(mention: number): ResourceRelation | undefined {
    if (this.selectedShieldings.some(s => s.childResourceID === mention)) {
      return undefined;
    }

    return this.allShieldings.find(r => r.childResourceID === mention)
      ?? this.resourceRelations.find(r =>
        r.childResourceID === mention &&
        (r.parentResourceType === ResourceType.Shutter || r.parentResourceType === ResourceType.Accelerator)
      )
      ?? this.resourceRelations.find(r => r.childResourceID === mention);
  }

  /**
   * Rehydrates a shielding relation from the full resource store so shared-parent metadata is available.
   * Mention-driven selections can arrive with partial child resources, which breaks shared shielding lookup.
   * @param option - The shielding or TOCA relation selected from work description or the mention picker.
   * @returns The same relation with its child resource refreshed from `this.resources` when possible.
   */
  private hydrateShieldingRelation(option: ResourceRelation): ResourceRelation {
    const childResource = this.resources?.find(r => r.id === option.childResourceID);
    if (childResource) {
      option.childResource = {
        ...childResource,
        parentResources: childResource.parentResources ?? option.childResource?.parentResources
      };
    }

    const parentResource = this.resources?.find(r => r.id === option.parentResourceID);
    if (parentResource) {
      option.parentResource = parentResource;
    }

    return option;
  }


  /**
   * Checks whether an amendment changed a field that is editable in the current amendment type.
   * Minor amendments suppress highlights for restricted fields because those controls are read-only.
   * @param field - Field to compare.
   * @returns True when the visible amendment field differs from the original RSF.
   */
  checkDiff(field: Field): boolean {
    const minorAmendmentFields = [Field.ASCCForms, Field.RelatedDocuments, Field.RelatedLinks, Field.WorkDescription, Field.InitDate, Field.EndDate];
    // Minor amendments must not present initialization differences in restricted fields as user changes.
    if (this.status?.isMinorAmendment && !minorAmendmentFields.includes(field)) {
      return false;
    }

    let rsf: any = this.rsf;
    let rsfAmendment: any = this.rsfAmendment;
    if (this.rsf && this.rsf.kind == Kind.RSFAmendment) {
      rsf = this.rsf?.rsf;
      rsfAmendment = this.rsf;
    }
    if (rsf && rsfAmendment && this.status?.isViewingAmendment) {
      return this.service?.checkDiff(field, rsf, rsfAmendment) ?? false;
    }
    return false;
  }

  /**
   * Creates an array of RSFResourceRelation objects based on provided resources and relation type.
   * If a relation already exists in rsf.rsfResourceRelations, uses that relation.
   * Otherwise, creates a new relation object with default values.
   *
   * @param locations - Array of Resource objects to create relations for
   * @param type - The type of resource relation to create
   * @returns Array of RSFResourceRelation objects corresponding to the provided resources
   */
  createResourceRelations = (locations: Resource[], type: RSFResourceRelationType): (RSFResourceRelation)[] => {
    const resourceRelations: (RSFResourceRelation)[] = [];
    locations.forEach(l => {
      var resourceRelation = this.rsfProxy?.rsfResourceRelations.find(r => r.resourceID == l.id && r.type == type);
      if (!resourceRelation) {
        resourceRelation = {
          id: 0,
          name: l.name ?? '',
          description: l.resourceType?.name ?? '',
          type: type,
          code: '',
          status: 1,
          resource: this.toRelationResource(l),
          resourceID: l.id,
          rsfID: this.rsfProxy?.kind == Kind.RSF ? this.rsfProxy?.id : 0,
          rsfAmendmentID: this.rsfProxy?.kind == Kind.RSFAmendment ? this.rsfAmendment?.id : 0,
          insertOrigin: l.insertOrigin,
          disabled: l.disabled ?? false,
        } as RSFResourceRelation;
      }
      if (resourceRelation)
        resourceRelations.push(resourceRelation);
    });
    return resourceRelations;
  };

  /**
   * Keeps new RSF resource relations saveable while the UI continues using full catalog resources.
   * @param resource - Catalog resource selected or inferred for Systems Affected.
   * @returns Minimal resource snapshot needed by RSF relation display and persistence.
   */
  private toRelationResource(resource: Resource): Resource {
    return {
      id: resource.id,
      name: resource.name,
      type: resource.type,
      description: resource.description,
      resourceTypeID: resource.resourceTypeID,
      rfSystem: resource.rfSystem,
      code: resource.code
    } as Resource;
  }


  /**
   * Handles changes to the RSF description field.
   *
   * Updates the description of the current RSF or RSF Amendment, triggers form validation,
   * and marks the form as dirty to indicate unsaved changes.
   *
   * @param e - The new description value to be set on the RSF proxy
   *
   * @remarks
   * This method:
   * - Updates the `rsfProxy.description` with the provided value
   * - Calls `formValidate()` to re-evaluate form validity (e.g., required field checks)
   * - Calls `setFormDirty()` to mark the form as having pending changes
   *
   * The method uses non-null assertion (!) on `rsfProxy` as it is expected to be initialized
   * before this handler is called.
   */
  descriptionChanged(e: any) {
    this.rsfProxy!.description = e;
    this.formValidate();
    this.setFormDirty();
  }


  /**
   * Handles the selection of a shielding resource relation and updates the selected shieldings and locations accordingly.
   *
   * - Adds the selected shielding to the `selectedShieldings` array if not already present.
   * - Orders the shieldings after addition.
   * - If the selected shielding is of type `TOCA`, finds related TOCA resources and appends them to `selectedLocations`.
   *   - Also adds the 'TopOff' resource to the selected locations if present.
   * - For other resource types, determines associated shutters and beamline relations to find related locations.
   *   - If no locations are found and `locationsIDs` are provided, parses and adds those locations.
   *   - If the option is shared, also includes shared locations.
   * - Updates the `selectedLocations` array with all determined locations.
   * - Emits newly inferred location IDs after rebuilding RSF relations so restrictions auto-populate.
   *
   * @param option The `ResourceRelation` object representing the shielding option selected by the user.
   */
  shieldingSelected(option: ResourceRelation): void {
    this.selectedShieldings ??= [];

    if (this.selectedShieldings.some(x => x.childResourceID === option.childResourceID)) {
      return;
    }

    option = this.hydrateShieldingRelation(option);
    const previousLocationIDs = this.getSelectedLocationIDs();
    this.selectedShieldings = [...this.selectedShieldings, option];
    this.orderShieldings();

    if (option.childResourceType === this.resourceTypeEnum.TOCA) {
      this.handleTocas(option);
    } else if (option.parentResourceType === ResourceType.Shutter) {
      this.handleShutterShielding(option);
    } else if (option.parentResourceType === ResourceType.Accelerator) {
      this.handleAcceleratorShielding(option);
    }

    this.createSelectedLocations(true, previousLocationIDs);
  }

  /**
     * Adds related TOCA locations and the TopOff resource to the selected locations list.
     *
     * Looks up the TOCA by its negated childResourceID, gathers its related beamline resources,
     * and persists them as RSF relations so locations and restrictions are added automatically.
     *
     * @param option - The selected TOCA-related resource relation.
     */
  private handleTocas(option: ResourceRelation): void {
    const tocaId = -(option.childResourceID ?? 0);
    const tocaResources = this.getTocaBeamlineResources(tocaId);

    const topOff = this.resources.find(r => r.code === 'TopOff');
    const topOffResources = topOff ? [this.withAutoOrigin(topOff)] : [];
    // Deduplicate the combined BRATOI/catalog/TopOff results before adding RSF relations.
    const resources = this.bratoiService.deduplicateResources([...tocaResources, ...topOffResources]);

    this.selectedLocations = this.utils.mergeUnique(
      this.selectedLocations,
      resources,
      'id'
    );

    this.mergeRSFRelationsByType(resources);
  }

  /**
   * Resolves every beamline that shares the selected TOCA through BRATOI or catalog relations.
   * @param tocaId - Positive TOCA identifier.
   * @returns Auto-origin resources that should be added to Systems Affected.
   */
  private getTocaBeamlineResources(tocaId: number): Resource[] {
    // The resolver returns one approved location per ID; RSF only adds its form-specific auto origin.
    return this.bratoiService
      .resolveRelatedResources(tocaId, this.bratois, this.tocas, this.resources)
      .map(r => this.withAutoOrigin(r));
  }

  /**
   * Finds a TOCA in the active TOCA store and the richer BRATOI TOCA collections.
   * @param tocaId - Positive TOCA identifier.
   * @param scopedLocations - Optional locations used to prefer the selected-location BRATOI row.
   * @returns Matching TOCA instances ordered by best available context.
   */
  private findToca(tocaId: number, scopedLocations: Resource[] = []): TOCA[] {
    const scopedIds = scopedLocations.map(l => l.id);
    const bratoiScoped = this.bratois
      ?.filter(b => scopedIds.includes(b.resourceId ?? 0) || scopedIds.includes(b.resource?.id ?? 0))
      ?.flatMap(b => b.tocas ?? [])
      ?.filter(t => t.id === tocaId) ?? [];
    const bratoiShared = this.bratois
      ?.flatMap(b => b.tocas ?? [])
      ?.filter(t => t.id === tocaId) ?? [];
    const catalog = this.tocas?.filter(t => t.id === tocaId) ?? [];

    return this.bratoiService.deduplicateTocas([...bratoiScoped, ...bratoiShared, ...catalog]);
  }

  /**
   * Adds shutter-related locations to the selected list and updates beamline locations accordingly.
   *
   * Retrieves locations via `getShutterLocations` or fallback, merges into `selectedLocations`,
   * then derives and adds beamlines to `selectedBeamlineLocations` while updating RSF relations.
   *
   * @param option - The selected shutter-related resource relation.
   */
  private handleShutterShielding(option: ResourceRelation): void {
    const shutterLocations = this.getShutterLocations(option);
    const fallbackLocations = this.getFallbackShutterLocations(option);
    // Merge relation-derived and shared fallback locations so one direct match does not hide shared beamlines.
    const locations = this.utils.mergeUnique(shutterLocations, fallbackLocations, 'id');

    this.selectedLocations = this.utils.mergeUnique(
      this.selectedLocations,
      locations,
      'id'
    );

    const beamlinesToAdd = this.getBeamlinesFromSelectedLocations();

    if (beamlinesToAdd.length) {
      this.selectedBeamlineLocations = beamlinesToAdd;
      this.mergeRSFRelations(beamlinesToAdd, RSFResourceRelationType.Beamline);
    }
  }

  /**
   * Adds accelerator-related locations to the selected list and updates RSF relations.
   *
   * Identifies shared parent resources, extracts location IDs, filters matching resources,
   * merges into `selectedAcceleratorLocations`, and updates `rsfProxy.rsfResourceRelations`.
   *
   * @param option - The selected accelerator-related resource relation.
   */
  private handleAcceleratorShielding(option: ResourceRelation): void {
    const parent = this.resources.find(r => r.id === option.parentResourceID);

    const parentsShared =
      option.childResource?.parentResources?.filter(
        p => p.parentResourceID !== parent?.id
      ) ?? [];

    const locationIDs = parentsShared.flatMap(
      p => this.utils.JSONparse(p.locationsIDs) as number[]
    );

    const locations = this.resources
      .filter(r => locationIDs.includes(r.id ?? 0))
      .map(r => this.withAutoOrigin(r));

    this.selectedAcceleratorLocations = this.utils.mergeUnique(
      this.selectedAcceleratorLocations,
      locations,
      'id'
    );

    this.mergeRSFRelations(locations, RSFResourceRelationType.Accelerator);
  }

  private getShutterLocations(option: ResourceRelation): Resource[] {
    const shutters = this.resourceRelations
      .filter(rr =>
        rr.childResourceID === option.childResourceID &&
        rr.parentResourceType === ResourceType.Shutter
      )
      .map(rr => rr.parentResource)
      .filter((r): r is Resource => !!r);

    const shutterIds = shutters.map(s => s.id ?? 0);

    return this.resourceRelations
      .filter(rr =>
        rr.childResourceType === ResourceType.Beamline &&
        shutterIds.includes(rr.parentResourceID ?? 0)
      )
      .map(rr => rr.childResource)
      .filter((r): r is Resource => !!r)
      .map(r => this.withAutoOrigin(r));
  }

  private getFallbackShutterLocations(option: ResourceRelation): Resource[] {
    const directLocationIDs = option.locationsIDs
      ? (this.utils.JSONparse(option.locationsIDs) as number[])
      : [];

    const directLocations = this.resources
      .filter(x => directLocationIDs.includes(x.id ?? 0))
      .map(r => this.withAutoOrigin(r));

    if (!option.shared) {
      return directLocations;
    }

    let sharedLocationIDs: number[] = [];
    option.sharedWith?.forEach(sw => {
      const ids = this.utils.JSONparse(sw) as number[];
      sharedLocationIDs = sharedLocationIDs.concat(ids);
    });

    sharedLocationIDs = [...new Set(sharedLocationIDs)];

    const sharedLocations = this.resources
      .filter(x => sharedLocationIDs.includes(x.id ?? 0))
      .map(r => this.withAutoOrigin(r));

    return this.utils.mergeUnique(
      directLocations,
      sharedLocations,
      'id'
    );
  }

  private getBeamlinesFromSelectedLocations(): Resource[] {
    const shutterIDs =
      this.selectedLocations
        .filter(l =>
          l.type !== ResourceType.Shutter &&
          l.parentResources?.some(pr => pr.parentResourceType === ResourceType.Shutter)
        )
        .flatMap(l => l.parentResources ?? [])
        .map(pr => pr?.parentResourceID ?? 0);

    const shuttersToAdd = this.resources
      .filter(r => shutterIDs.includes(r.id ?? 0))
      .map(r => this.withAutoOrigin(r));

    // Hydrate childResources before accessing childResource
    const hydratedShutters = shuttersToAdd.map(s => ({
      ...s,
      childResources: s.childResources?.map(cr => ({
        ...cr,
        childResource: cr.childResource ||
          this.resources?.find(r => r.id === cr.childResourceID)
      }))
    }));

    return hydratedShutters
      .flatMap(s => s.childResources ?? [])
      .filter(c => c.childResourceType === ResourceType.Beamline && c.childResource)
      .map(c => c.childResource!)
      .map(b => ({
        ...this.withAutoOrigin(b),
        name: !b.name?.includes('/')
          ? `${b.name} / ${b.parentResources?.at(-1)?.parentResourceName ?? ''}`
          : b.name
      }));
  }

  private mergeRSFRelations(resources: Resource[], type: RSFResourceRelationType): void {
    if (!this.rsfProxy) {
      return;
    }

    this.ensureSystemTypeChecked(type);
    const current = this.rsfProxy.rsfResourceRelations ?? [];
    const additions = this.createResourceRelations(resources, type);

    this.rsfProxy.rsfResourceRelations = this.utils.mergeUnique(
      current,
      additions,
      'resourceID'
    );
  }

  /**
   * Turns on the parent system checkbox when shielding or TOCA selection auto-adds locations.
   * @param type - Systems Affected group receiving auto-added locations.
   */
  private ensureSystemTypeChecked(type: RSFResourceRelationType): void {
    if (!this.rsfProxy) {
      return;
    }

    if (type === RSFResourceRelationType.Beamline) {
      this.rsfProxy.beamLine = true;
      this.systemsAffected?.ctrlBeamline.setValue(true, { emitEvent: false });
    }

    if (type === RSFResourceRelationType.Accelerator) {
      this.rsfProxy.accelerator = true;
      this.systemsAffected?.ctrlAccelerator.setValue(true, { emitEvent: false });
    }
  }

  /**
   * Persists auto-added resources under the matching RSF relation group.
   * @param resources - Related resources inferred from a selected shielding or TOCA.
   */
  private mergeRSFRelationsByType(resources: Resource[]): void {
    const beamlineResources = resources.filter(r => this.getRSFResourceRelationType(r) === RSFResourceRelationType.Beamline);
    const acceleratorResources = resources.filter(r => this.getRSFResourceRelationType(r) === RSFResourceRelationType.Accelerator);

    // Persist related locations so auto-added shielding/TOCA context reaches the restriction table.
    if (beamlineResources.length) {
      this.mergeRSFRelations(beamlineResources, RSFResourceRelationType.Beamline);
    }

    if (acceleratorResources.length) {
      this.mergeRSFRelations(acceleratorResources, RSFResourceRelationType.Accelerator);
    }
  }

  /**
   * Classifies a resource into the RSF relation bucket used by the Systems Affected controls.
   * @param resource - Resource being added to the RSF.
   * @returns The RSF resource relation type for the resource.
   */
  private getRSFResourceRelationType(resource: Resource): RSFResourceRelationType {
    return resource.type === ResourceType.Accelerator || resource.type === ResourceType.AcceleratorLocation
      ? RSFResourceRelationType.Accelerator
      : RSFResourceRelationType.Beamline;
  }

  private withAutoOrigin(resource: Resource): Resource {
    return {
      ...resource,
      insertOrigin: InsertOrigin.Auto
    };
  }

  /**
   * Removes a shielding resource relation from the selected shieldings array
   * and updates related shielding data.
   *
   * @param shielding - The ResourceRelation object to be removed from selected shieldings
   * @remarks
   * This method:
   * 1. Removes the specified shielding from selectedShieldings array if found
   * 2. Reorders remaining shieldings
   * 3. Reloads related shielding data
   */
  removeShl(shielding: ResourceRelation): void {
    const index = this.selectedShieldings.indexOf(shielding);
    if (index >= 0) {
      this.selectedShieldings.splice(index, 1);
      // if (!this.selectedShieldings.filter((x) => x.childResourceType === 0).length) {
      //   // TOCA
      //   this.checkTocaMovCtrl?.setValue(false);
      // }
      this.orderShieldings();
    }
    this.loadRelatedShieldings();
    this.rsf!.rsfResourceRelations;
  }

  orderShieldings() {
    let i = 0;
    this.selectedShieldings.map((s) => {
      s.position = i;
      i++;
    });
  }

  getRoleCodes(user: User): string | undefined {
    return user.userRole?.map(r => r.role?.code).join('/');
  }

  //#region Requesters
  loadRequesters() {
    this.filteredRequesters = this.filterRequesters();
  }

  onRequesterSelected(e: MatAutocompleteSelectedEvent) {
    this.setFormDirty();
    this.formValidate();
    this.rsfProxy!.requesterID = e.option.value.id;
    this.rsfProxy!.requester = this.users.find(u => u.id === e.option.value.id);
  }

  /**
   * Filters users by RSFRequester privilege and optionally by name search term.
   * @param term - Optional search term to filter users by name
   * @returns Array of User objects that have RSFRequester privilege and match the search term
   */
  private filterRequesters(term?: string): User[] {
    const normalized = term?.trim().toLowerCase();

    return this.getActiveUsers().filter(u => {
      if (!this.hasPrivilegeByUser(PrivilegeEnum.RSFRequester, u.id ?? 0)) {
        return false;
      }

      if (!normalized) return true;

      const name = (u.name ?? '').toLowerCase();
      return name.includes(normalized);
    });
  }

  onRequesterChange(_event: Event): void {
    const rawValue = this.ctrlRequester.value ?? '';
    this.filteredRequesters = this.filterRequesters(rawValue.toString());
    this.rsfProxy!.requesterID = rawValue.id;
    this.rsfProxy!.requester = this.users.find(u => u.id === rawValue.id);
    this.formValidate();
  }

  onInitDateChanged(e: MatDatepickerInputEvent<Date>) {
    this.rsfProxy!.estimatedStartOn = e.value;
    this.setFormDirty();
    this.formValidate();
  }

  /**
   * Updates the estimated end date and validates the form.
   * @param e - The datepicker event containing the new end date value
   */
  onEndDateChanged(e: MatDatepickerInputEvent<Date>) {
    this.rsfProxy!.estimatedEndOn = e.value;
    this.setFormDirty();
    this.formValidate();
  }

  /**
   * Updates role notifications and marks the form as modified.
   * @param e - Array of RSFRoleNotification objects to be set
   */
  onRoleNotificationsChanged(e: RSFRoleNotification[]) {
    this.ctrlRoleNotifications.setValue(e);
    this.ctrlRoleNotifications.markAsDirty();
    this.ctrlRoleNotifications.markAsTouched();
    this.rsfProxy!.rsfRoleNotifications = e;
    this.setFormDirty();
  }

  getLocationIDs() {
    this.locationIDs = this.rsfProxy?.rsfResourceRelations.map(rr => rr.resourceID);
  }

  /**
   * Updates form restrictions and applies them to the RSF.
   * @param e - Array of FormRestriction objects representing the updated restrictions
   */
  onRestrictionsChanged(e: FormRestriction[]) {
    this.formRestrictions = e;
    this.ctrlRestrictions.setValue(this.formRestrictions);
    this.ctrlRestrictions.markAsTouched();
    this.rsfProxy!.rsfRestrictions = RSFRestrictionMapper.fromForms(this.formRestrictions, this.rsf as RSF, this.rsfAmendment);
    if (!this.formRestrictions.length) this.ctrlRestrictions.setErrors({ required: true });
    this.rsfProxy!.rsfRestrictions = RSFRestrictionMapper.fromForms(e, this.rsf as RSF, this.rsfAmendment);
    this.formRestrictions = RSFRestrictionMapper.fromRSF(this.rsfProxy?.rsfRestrictions, this.rsf?.rsfRestrictions);
    this.setValues();
    this.setFormDirty();
  }


  /**
   * Handles changes to the systems affected in an RSF or RSF Amendment.
   * Updates the resource relations, form controls, locations, and related shielding information.
   *
   * @param rsf - The RSF or RSF Amendment object that was modified
   *
   * The method performs the following operations:
   * - Updates resource relation IDs based on the kind of RSF
   * - Enables/disables child and parent form controls based on the selected Systems Affected conditions
   * - Retrieves location IDs
   * - Updates selected beamline and accelerator locations
   * - Loads related shielding information
   * - Creates shielding groups
   * - Validates the form
   * - Marks the form as dirty
   */
  onSystemsAffectedChanged(rsfProxy: RSF | RSFAmendment) {
    // Systems Affected is read-only during a Minor Amendment, including programmatic child emissions.
    if (this.status?.isMinorAmendment) {
      return;
    }

    const changes = detectChanges(rsfProxy, this.rsfProxyUnchanged);
    console.log('Systems Affected Changes:', changes);
    const emittedLocationAddedIDs = this.pendingSystemsAffectedLocationAddedIDs;
    this.pendingSystemsAffectedLocationAddedIDs = [];

    this.rsfProxy = rsfProxy;
    this.rsfProxyUnchanged = this.utils.cloneDeep(rsfProxy);

    if (rsfProxy.kind == Kind.RSF) {
      this.rsfProxy.rsfResourceRelations.forEach(rr => rr.rsfID = this.rsfProxy?.id);
    }
    else if (rsfProxy.kind == Kind.RSFAmendment) {
      this.rsfProxy.rsfResourceRelations.forEach(rr => rr.rsfAmendmentID = this.rsfProxy?.id);
    }

    // Keep the dropdown state aligned with the same Systems Affected rule used by the template.
    if (!this.hasSystemsAffectedBeyondPps()) {
      this.ctrlChildForms.disable();
      this.ctrlParentForms.disable();
    }
    else {
      this.ctrlChildForms.enable();
      this.ctrlParentForms.enable();
    }
    this.getLocationIDs();

    this.createSelectedLocations();
    if (emittedLocationAddedIDs.length) {
      // Preserve the resource-chip emitted ID; selected arrays may already be mutated before this rebuild runs.
      this.locationAddedIDs = [...new Set([...emittedLocationAddedIDs, ...(this.locationAddedIDs ?? [])])];
    }

    this.rsfProxy.rfSystemLocation = this.rsfProxy.rsfResourceRelations.map(r => this.resources?.find(res => res.id == r.resourceID) ?? r.resource).some(r => {
      if (r.rfSystem) return true;
      r.parentResources?.forEach(pr => {
        pr.parentResource = this.resources.find(res => res.id == pr.parentResourceID);
      });
      if (r.parentResources?.some(pr => pr.parentResource?.rfSystem)) return true;
      return false;
    });

    this.loadRelatedShieldings();
    this.createShieldingGroups();
    this.notifications?.checkConditions();
    this.formValidate();
    this.setFormDirty();
    this.systemsAffectedChanged.emit({ rsfProxy: this.rsfProxy, changes });
  }

  /**
   * Disables accelerator and beamline form controls if any of their selected locations are restricted.
   *
   * Checks if any selected accelerator or beamline locations have the `disabled` flag set (indicating
   * a restriction), and disables the corresponding form controls if found. Otherwise, enables them.
   *
   * @remarks
   * This method enforces UI state consistency by preventing users from modifying system selections
   * when restricted resources are involved.
   */
  checkSelectedLocations() {
    // Never re-enable beamline/accelerator controls during a minor amendment
    if (this.status?.isMinorAmendment) {
      this.systemsAffected?.ctrlAccelerator.disable();
      this.systemsAffected?.ctrlBeamline.disable();
      return;
    }

    if (this.selectedAcceleratorLocations.some(l => l.disabled) || this.disabled) {
      this.systemsAffected?.ctrlAccelerator.disable();
    }
    else {
      this.systemsAffected?.ctrlAccelerator.enable();
    }

    if (this.selectedBeamlineLocations.some(l => l.disabled) || this.disabled) {
      this.systemsAffected?.ctrlBeamline.disable();
    }
    else {
      this.systemsAffected?.ctrlBeamline.enable();
    }
  }

  /**
   * Determines if the form can be submitted.
   * @returns true if RSF is in Draft status, user has submit privilege, and form is being created or edited.
   */
  canSubmit() {
    return this.rsf?.status == FormStatusEnum.Draft && this.privileges?.canSubmit &&
      (this.status?.isCreating || this.status?.isEditing);
  }

  //#region Tester tools: auto-populate (Phase 1)
  // Non-production tester tooling that mirrors the SCF form's auto-fill (see
  // docs/scf-test-automation.md). It fills the Section 1 required fields with valid dummy data so a
  // tester can bring an RSF to a submittable Draft in one click, exercising the same handlers a real
  // user's clicks would. It never saves; Save/Submit stay the tester's action.

  /**
   * Role IDs accepted for the RW approver/designee requester on the Shielding Happy path: RWAPI (20)
   * or RWADes (1110). Note: the Roles enum labels 1110 as "RWPADes"; per the role catalog ID 1110 is
   * RWADes (the 1111/"RWPADes" role is an unused typo and is intentionally not used). Matched by role
   * ID against the requester's roles.
   */
  private static readonly RWA_ROLE_IDS: number[] = [Roles.RWAPI, 1110];
  /** Role ID accepted for the PPS engineer requester on the PPS Happy path: PPSEng (16, ex-RSSEng). */
  private static readonly PPS_ENG_ROLE_IDS: number[] = [Roles.PPSEng];

  /**
   * Whether the tester-only auto-populate menu should be shown. Gated defensively: only on
   * non-production hosts, for System Administrators, and only when the shared Tester Tools master
   * switch (rsf-main) is ON. Never surfaces for real users in production.
   */
  get showTestTools(): boolean {
    return !this.environmentService.isProduction() && this.isSA() && this.testerToolsService.enabled;
  }

  /**
   * Tester convenience: fills the Section 1 fields for one of four workflow paths so the form
   * becomes a submittable Draft in one click. All four select Beamline + Accelerator (a
   * pseudo-random location each) and enter custom work-description text plus at least one shielding
   * chip (via the description "@"-mention) when the chosen locations offer any. The two "complex"
   * paths also add a custom free-text restriction.
   *
   * - 'shielding-happy'  : Shielding → Remove/Replace; requester holding an RWAPI/RWADes role.
   * - 'shielding-complex': Shielding → Modification (ARC); a requester who does NOT hold an RWA role.
   * - 'pps-happy'        : PPS → Maintenance; requester holding the PPSEng role.
   * - 'pps-complex'      : PPS → PPS Modification (ARC); a requester who does NOT hold PPSEng.
   *
   * Requester roles are best-effort (matched by role code); if no matching user exists it falls back
   * to any RSF requester, so the required field is always satisfied.
   */
  autoPopulate(preset: RsfTesterPreset): void {
    if (!this.rsfProxy || !this.showTestTools || this.disabled) return;

    // 1. Systems Affected: type-of-work checkboxes + Beamline/Accelerator toggles, applied through
    //    the systems-affected component's own onSystemChanged() handler.
    this.applyAutoWorkType(preset);

    // 2. Locations: a pseudo-random beamline + accelerator location, through the real
    //    onBeamlineLocationsChanged/onAcceleratorLocationsChanged handlers.
    const locationIDs = this.selectAutoLocations();

    // 3. Requester: role-appropriate per preset, with graceful fallback.
    this.setAutoRequester(preset);

    // 4. Estimated dates: start = today, completion = today + 7.
    this.fillAutoEstimatedDates();

    // 5. Work Description + a shielding "@"-mention chip (if the locations offer any shielding).
    this.enterAutoDescriptionAndShielding(preset);

    // 6. Custom restriction: only the two complex paths, and only once the form is a saved Draft
    //    (the Safety Controls table renders on *ngIf="rsf").
    if (preset === 'shielding-complex' || preset === 'pps-complex') {
      this.addAutoCustomRestriction(preset);
    }

    // 7. Trigger standard (location-derived) restriction auto-population LAST. The restriction-table's
    //    child autocomplete seeds these off `locationAddedIDs` on a microtask; several earlier steps
    //    clear that field (onRestrictionsChanged -> setValues() sets it to [], and each location emit
    //    overwrites it with a single id), so setting both location ids here (after everything else)
    //    is what actually reaches change detection. The standards then merge on top of any custom
    //    restriction added in step 6.
    if (locationIDs.length) this.locationAddedIDs = locationIDs;

    this.formValidate();
    this.setFormDirty();
  }

  /**
   * Clears the Section 1 fields the presets populate (systems affected + locations, shielding,
   * requester, description, restrictions) and resets the estimated dates to their defaults
   * (today / today + 7). Does not save.
   */
  resetFields(): void {
    if (!this.rsfProxy || !this.showTestTools || this.disabled) return;

    const sa = this.systemsAffected;
    if (sa) {
      [
        sa.ctrlBeamline, sa.ctrlAccelerator, sa.ctrlShielding, sa.ctrlShlRemoveReplace,
        sa.ctrlShlModification, sa.ctrlShlModificationARC, sa.ctrlShlModificationBRC,
        sa.ctrlPPS, sa.ctrlPPSMaintenance, sa.ctrlPPSModification, sa.ctrlPPSModificationARC,
        sa.ctrlPPSModificationBRC,
      ].forEach(c => c.setValue(false));
      // Turning Beamline/Accelerator off drops their resource relations and location arrays.
      sa.onSystemChanged();
    }

    this.selectedShieldings = [];
    this.mentionsChanged([]);

    this.ctrlRequester.setValue(null);
    this.rsfProxy.requesterID = null;
    this.rsfProxy.requester = undefined;

    this.fillAutoEstimatedDates();

    this.rsfProxy.description = '';
    this.descriptionChanged('');
    this.autoFillEntryCount = 0;

    if (this.rsf) this.onRestrictionsChanged([]);

    this.formValidate();
    this.setFormDirty();
  }

  /** Sets the type-of-work + Beamline/Accelerator checkboxes for the preset, then applies them. */
  private applyAutoWorkType(preset: RsfTesterPreset): void {
    const sa = this.systemsAffected;
    if (!sa) return;

    sa.ctrlBeamline.setValue(true);
    sa.ctrlAccelerator.setValue(true);

    switch (preset) {
      case 'shielding-happy':
        sa.ctrlShielding.setValue(true);
        sa.ctrlShlRemoveReplace.setValue(true);
        break;
      case 'shielding-complex':
        sa.ctrlShielding.setValue(true);
        sa.ctrlShlModification.setValue(true);
        // Modification requires ARC or BRC; pick one pseudo-randomly (only ever set true, so a prior
        // run's choice is kept rather than toggled off).
        if (Math.random() < 0.5) sa.ctrlShlModificationARC.setValue(true);
        else sa.ctrlShlModificationBRC.setValue(true);
        break;
      case 'pps-happy':
        sa.ctrlPPS.setValue(true);
        sa.ctrlPPSMaintenance.setValue(true);
        break;
      case 'pps-complex':
        sa.ctrlPPS.setValue(true);
        sa.ctrlPPSModification.setValue(true);
        // PPS Modification requires ARC or BRC; pick one pseudo-randomly (only ever set true).
        if (Math.random() < 0.5) sa.ctrlPPSModificationARC.setValue(true);
        else sa.ctrlPPSModificationBRC.setValue(true);
        break;
    }

    // Copies the control values into the RSF proxy and emits changed → onSystemsAffectedChanged.
    sa.onSystemChanged();
  }

  /**
   * Selects one pseudo-random beamline location and one accelerator location (all presets), through
   * the same handlers `resource-chips` fires. Returns the selected location resource ids so the
   * caller can trigger standard-restriction auto-population for them. Uses InsertOrigin.Manual so the
   * chips render like a normal user selection (grey/black) rather than the red "auto-added" style.
   */
  private selectAutoLocations(): number[] {
    const sa = this.systemsAffected;
    if (!sa) return [];

    const ids: number[] = [];

    // Additive: merge the new pick with any locations already selected (e.g. from a prior preset
    // run) rather than replacing them, mirroring resource-chips' cumulative onEdit array. Exclude
    // already-selected ids from the pick so re-runs add variety instead of the same location.
    const existingBeamline = this.selectedBeamlineLocations ?? [];
    const beamline = this.pickAutoBeamlineLocation(existingBeamline.map(b => b.id));
    if (beamline) {
      sa.onBeamlineLocationsChanged(
        this.utils.mergeUnique(existingBeamline, [this.withManualOrigin(beamline)], 'id')
      );
      if (beamline.id != null) ids.push(beamline.id);
    }

    const existingAccelerator = this.selectedAcceleratorLocations ?? [];
    const accelerator = this.pickAutoAcceleratorLocation(existingAccelerator.map(a => a.id));
    if (accelerator) {
      sa.onAcceleratorLocationsChanged(
        this.utils.mergeUnique(existingAccelerator, [this.withManualOrigin(accelerator)], 'id')
      );
      if (accelerator.id != null) ids.push(accelerator.id);
    }

    return ids;
  }

  /** Marks a resource as a manual (user-style) selection so its chip renders grey/black, not red. */
  private withManualOrigin(resource: Resource): Resource {
    return { ...resource, insertOrigin: InsertOrigin.Manual };
  }

  /** A pseudo-random beamline-type resource, excluding already-selected ids (variety over first). */
  private pickAutoBeamlineLocation(excludeIds: (number | undefined)[] = []): Resource | undefined {
    const typeIDs = this.systemsAffected?.beamlineResourceTypeIDs?.length
      ? this.systemsAffected.beamlineResourceTypeIDs
      : [ResourceType.Beamline];
    const candidates = (this.resources ?? []).filter(
      r => r.type != null && typeIDs.includes(r.type) && r.status !== 0 && !!r.name && !excludeIds.includes(r.id)
    );
    return this.pickRandom(candidates);
  }

  /** A pseudo-random accelerator location (prefers AcceleratorLocation), excluding already-selected ids. */
  private pickAutoAcceleratorLocation(excludeIds: (number | undefined)[] = []): Resource | undefined {
    const locations = (this.resources ?? []).filter(
      r => r.type === ResourceType.AcceleratorLocation && r.status !== 0 && !!r.name && !excludeIds.includes(r.id)
    );
    if (locations.length) return this.pickRandom(locations);

    const typeIDs = this.systemsAffected?.acceleratorResourceTypeIDs?.length
      ? this.systemsAffected.acceleratorResourceTypeIDs
      : [ResourceType.Accelerator];
    const candidates = (this.resources ?? []).filter(
      r => r.type != null && typeIDs.includes(r.type) && r.status !== 0 && !!r.name && !excludeIds.includes(r.id)
    );
    return this.pickRandom(candidates);
  }

  /** Sets the requester to a role-appropriate user for the preset, falling back gracefully. */
  private setAutoRequester(preset: RsfTesterPreset): void {
    const pool = this.requesterPool();
    let user: User | undefined;

    switch (preset) {
      case 'shielding-happy':
        user = pool.find(u => this.userHasRole(u, RsfMainS1Component.RWA_ROLE_IDS)) ?? this.pickRandom(pool);
        break;
      case 'shielding-complex':
        user = this.pickRandom(pool.filter(u => !this.userHasRole(u, RsfMainS1Component.RWA_ROLE_IDS))) ?? this.pickRandom(pool);
        break;
      case 'pps-happy':
        user = pool.find(u => this.userHasRole(u, RsfMainS1Component.PPS_ENG_ROLE_IDS)) ?? this.pickRandom(pool);
        break;
      case 'pps-complex':
        user = this.pickRandom(pool.filter(u => !this.userHasRole(u, RsfMainS1Component.PPS_ENG_ROLE_IDS))) ?? this.pickRandom(pool);
        break;
    }

    if (user?.id) this.setAutoRequesterValue(user);
  }

  /** The candidate requester pool: users with the RSF Requester privilege, else any user. */
  private requesterPool(): User[] {
    const requesters = this.users.filter(u => !!u.id && this.hasPrivilegeByUser(PrivilegeEnum.RSFRequester, u.id!));
    return requesters.length ? requesters : this.users;
  }

  /** Whether a user holds any of the given role IDs. */
  private userHasRole(user: User, roleIds: number[]): boolean {
    return user.userRole?.some(ur => ur.roleID != null && roleIds.includes(ur.roleID)) ?? false;
  }

  /** Writes the chosen requester into the control + RSF proxy, the same way onRequesterSelected does. */
  private setAutoRequesterValue(user: User): void {
    const full = this.users.find(u => u.id === user.id) ?? user;
    if (!this.filteredRequesters?.some(r => r.id === full.id)) {
      this.filteredRequesters = [full, ...(this.filteredRequesters ?? [])];
    }
    this.ctrlRequester.setValue(full);
    this.rsfProxy!.requesterID = full.id ?? null;
    this.rsfProxy!.requester = full;
  }

  /** Estimated start = today, completion = today + 7 days. */
  private fillAutoEstimatedDates(): void {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 7);
    this.ctrlInitDate.setValue(start);
    this.rsfProxy!.estimatedStartOn = start;
    this.ctrlEndDate.setValue(end);
    this.rsfProxy!.estimatedEndOn = end;
  }

  /**
   * Enters custom work-description text plus, when the selected locations offer any shielding, one
   * shielding chip via the "@"-mention path. mentionsChanged() adds the shielding (and pulls in its
   * locations/restrictions); the description carries the matching mention span markup so it renders
   * the same way a manually-typed mention would.
   *
   * Additive across runs: the description entry is appended (numbered) rather than overwriting, and
   * the shielding is passed as the union of already-selected + new so a prior run's chip survives.
   * A shielding not already selected is preferred so re-runs add a new chip instead of a duplicate.
   */
  private enterAutoDescriptionAndShielding(preset: RsfTesterPreset): void {
    this.loadRelatedShieldings();

    const takenIds = new Set((this.selectedShieldings ?? []).map(s => s.childResourceID));
    const named = (this.allShieldings ?? []).filter(s => s.childResourceID != null && !!s.childResourceName);
    // Prefer a not-yet-selected positive-id shielding (a real chip, not a TOCA); then any positive-id
    // shielding; then any not-yet-selected named entry; then any named entry.
    const chosen =
      named.find(s => (s.childResourceID ?? 0) > 0 && !takenIds.has(s.childResourceID)) ??
      named.find(s => (s.childResourceID ?? 0) > 0) ??
      named.find(s => !takenIds.has(s.childResourceID)) ??
      named[0];

    if (chosen?.childResourceID != null) {
      const existingIds = (this.selectedShieldings ?? []).map(s => s.childResourceID);
      const ids = Array.from(new Set([...existingIds, chosen.childResourceID]));
      this.mentionsChanged(ids);
    }

    const body = chosen
      ? `Affected shielding entered via @-mention: ${this.buildShieldingMentionSpans([chosen])}.`
      : 'No shielding available for the selected locations.';
    const entry = `${++this.autoFillEntryCount}. Auto-populated ${this.presetLabel(preset)} for testing. ${body}`;
    // Append (do not overwrite) so repeated auto-fill runs keep their history. Each entry is its own
    // paragraph so the mention spans survive the comments-box (Quill) re-parse.
    const existing = (this.rsfProxy!.description ?? '').trim();
    const html = `${existing}<p>${entry}</p>`;
    this.rsfProxy!.description = html;
    this.descriptionChanged(html);
  }

  /** Adds one custom free-text restriction to the Safety Controls table (complex presets). */
  private addAutoCustomRestriction(preset: RsfTesterPreset): void {
    if (!this.rsf) return;
    const custom = {
      id: 0,
      formID: this.rsf.id,
      text: `Auto-populated custom restriction (${this.presetLabel(preset)}).`,
    } as FormRestriction;
    this.onRestrictionsChanged([...(this.formRestrictions ?? []), custom]);
  }

  /** Human-readable label for a preset, used in the auto-generated description. */
  private presetLabel(preset: RsfTesterPreset): string {
    switch (preset) {
      case 'shielding-happy': return 'Shielding Happy Path';
      case 'shielding-complex': return 'Shielding Complex';
      case 'pps-happy': return 'PPS Happy Path';
      case 'pps-complex': return 'PPS Complex';
    }
  }

  /** Returns a pseudo-random element of `items`, or undefined when empty. */
  private pickRandom<T>(items: T[]): T | undefined {
    if (!items?.length) return undefined;
    return items[Math.floor(Math.random() * items.length)];
  }

  /**
   * Builds the comma-separated "@"-mention spans for the given shielding/TOCAs, matching the markup
   * the comments-box editor produces (StyledMentionBlot) so the mention survives re-parsing and its
   * KE Text / Windchill-links menu works. Adapted from the SCF auto-fill (scf-v2-main-s1).
   */
  private buildShieldingMentionSpans(shieldings: ResourceRelation[]): string {
    return shieldings
      .map(s => {
        const id = s.childResourceID;
        const name = this.escapeAttr(s.childResourceName ?? `Shielding ${id}`);
        const resource = this.resources?.find(x => x.id === id);
        const hasTitle = !!resource?.keText;
        const hasLinks = !!resource?.windchillLinks?.length;
        const title = hasTitle ? ` data-title="${this.escapeAttr(resource!.keText!)}"` : '';
        const links = hasLinks
          ? ` data-windchill-links="${this.escapeAttr(JSON.stringify(resource!.windchillLinks))}"`
          : '';
        const color = this.mentionColor(s);
        const style =
          `color: ${color}; font-weight: bold;` +
          (hasTitle || hasLinks ? ' cursor: pointer; text-decoration: underline;' : '');
        return (
          `<span class="mention" data-denotation-char="" data-id="${id}" ` +
          `data-value="${name}" data-color="${color}"${title}${links} ` +
          `style="${style}">${name}</span>`
        );
      })
      .join(', ');
  }

  /** Mirrors the comments-box dropdown's colour logic: red when shared, orange for TOCAs, else #333. */
  private mentionColor(s: ResourceRelation): string {
    const isShared =
      s.parentResourceType === ResourceType.Shutter ? !!s.shared : (s.sharedWith?.length ?? 0) > 0;
    if (isShared) return 'red';
    if ((s.childResourceType ?? 0) > 99) return 'darkorange';
    return '#333';
  }

  /** Escapes a string for safe inclusion in an HTML attribute. */
  private escapeAttr(value: string): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
  //#endregion

}

/** The four RSF auto-populate workflow paths offered by the tester tooling (Phase 1). */
export type RsfTesterPreset = 'shielding-happy' | 'shielding-complex' | 'pps-happy' | 'pps-complex';
